import {
    CASE_TEXT_COUNT,
    CrosswordDirection,
    CrosswordLine,
    CrosswordPuzzle,
    Deduction,
    lineSentence,
    linesOfCell,
} from "./crosswordGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

/*
 * The words of the Baker Street Math Crossword (ADR 0134). The engine (crosswordGame.ts)
 * hands back cells, values and lines; this puts them in the player's language. Every key
 * is written out here as a `labelKey` so StringKeys.test.js sees it, as QuizPage's modes do.
 *
 * The cases are original. Each title and brief uses only what Conan Doyle's stories hold -
 * Holmes, Dr Watson, Mrs Hudson, Inspector Lestrade, 221B Baker Street, gaslight and fog -
 * and none is the title of a story, which the text test checks against a list of them. A
 * title never says what a missing number is, and Holmes says "Elementary", which is the
 * word the stories give him, never the line they do not.
 */

const CASES: { title: { labelKey: StringKey }; brief: { labelKey: StringKey } }[] = [
    { title: { labelKey: "flashCards.crosswordCase1Title" }, brief: { labelKey: "flashCards.crosswordCase1Brief" } },
    { title: { labelKey: "flashCards.crosswordCase2Title" }, brief: { labelKey: "flashCards.crosswordCase2Brief" } },
    { title: { labelKey: "flashCards.crosswordCase3Title" }, brief: { labelKey: "flashCards.crosswordCase3Brief" } },
    { title: { labelKey: "flashCards.crosswordCase4Title" }, brief: { labelKey: "flashCards.crosswordCase4Brief" } },
    { title: { labelKey: "flashCards.crosswordCase5Title" }, brief: { labelKey: "flashCards.crosswordCase5Brief" } },
    { title: { labelKey: "flashCards.crosswordCase6Title" }, brief: { labelKey: "flashCards.crosswordCase6Brief" } },
    { title: { labelKey: "flashCards.crosswordCase7Title" }, brief: { labelKey: "flashCards.crosswordCase7Brief" } },
    { title: { labelKey: "flashCards.crosswordCase8Title" }, brief: { labelKey: "flashCards.crosswordCase8Brief" } },
    { title: { labelKey: "flashCards.crosswordCase9Title" }, brief: { labelKey: "flashCards.crosswordCase9Brief" } },
    { title: { labelKey: "flashCards.crosswordCase10Title" }, brief: { labelKey: "flashCards.crosswordCase10Brief" } },
    { title: { labelKey: "flashCards.crosswordCase11Title" }, brief: { labelKey: "flashCards.crosswordCase11Brief" } },
    { title: { labelKey: "flashCards.crosswordCase12Title" }, brief: { labelKey: "flashCards.crosswordCase12Brief" } },
];

/** A cell's place in a line, said to a screen reader: the first, second and third numbers, then the answer. */
const POSITION_KEYS = [
    { labelKey: "flashCards.crosswordPositionFirst" },
    { labelKey: "flashCards.crosswordPositionSecond" },
    { labelKey: "flashCards.crosswordPositionThird" },
] satisfies { labelKey: StringKey }[];

const LINE_KEYS = {
    across: { labelKey: "flashCards.crosswordLineAcross" },
    down: { labelKey: "flashCards.crosswordLineDown" },
} satisfies Record<CrosswordDirection, { labelKey: StringKey }>;

const TAG_KEYS = {
    across: { labelKey: "flashCards.crosswordTagAcross" },
    down: { labelKey: "flashCards.crosswordTagDown" },
} satisfies Record<CrosswordDirection, { labelKey: StringKey }>;

const ANSWER_KEY = { labelKey: "flashCards.crosswordPositionAnswer" } satisfies { labelKey: StringKey };

export interface CrosswordText {
    caseTitle(index: number): string;
    caseBrief(index: number): string;
    /** "Across 2" */
    lineLabel(line: CrosswordLine): string;
    /** "A2": the small tag in the corner of a line's first cell. */
    lineTag(line: CrosswordLine): string;
    /** "Across 2, first number; Down 1, second number" */
    cellLabel(puzzle: CrosswordPuzzle, cell: number): string;
    /** "Down 2 says 6 × ? = 42. Only 7 works: 6 × 7 = 42." */
    hintText(puzzle: CrosswordPuzzle, deduction: Deduction): string;
    /** "Across 1: 3 + blank = 8", for a screen reader. */
    lineReading(puzzle: CrosswordPuzzle, line: CrosswordLine, entries: readonly (number | null)[]): string;
}

/** The words, from a `t`. The hook below is this for the interface language; tests give it the English one. */
export function makeCrosswordText(t: (key: StringKey) => string): CrosswordText {
    const lineLabel = (line: CrosswordLine) =>
        format(t(LINE_KEYS[line.direction].labelKey), { n: line.number });
    const positionName = (line: CrosswordLine, position: number) =>
        t(position === line.cells.length - 1 ? ANSWER_KEY.labelKey : POSITION_KEYS[position].labelKey);
    return {
        caseTitle: (index) => t(CASES[index % CASE_TEXT_COUNT].title.labelKey),
        caseBrief: (index) => t(CASES[index % CASE_TEXT_COUNT].brief.labelKey),
        lineLabel,
        lineTag: (line) => format(t(TAG_KEYS[line.direction].labelKey), { n: line.number }),
        cellLabel: (puzzle, cell) =>
            linesOfCell(puzzle, cell)
                .map(({ line, position }) =>
                    format(t("flashCards.crosswordCellLabelTemplate"), {
                        line: lineLabel(line),
                        position: positionName(line, position),
                    })
                )
                .join("; "),
        hintText: (puzzle, deduction) => {
            const line = puzzle.lines[deduction.line];
            return format(t("flashCards.crosswordHintTemplate"), {
                line: lineLabel(line),
                open: lineSentence(line, (cell) => (cell === deduction.cell ? "?" : String(puzzle.solution[cell]))),
                value: deduction.value,
                closed: lineSentence(line, (cell) => String(puzzle.solution[cell])),
            });
        },
        lineReading: (puzzle, line, entries) =>
            format(t("flashCards.crosswordLineReadingTemplate"), {
                line: lineLabel(line),
                sentence: lineSentence(line, (cell) => {
                    const value = puzzle.blanks.includes(cell) ? entries[cell] : puzzle.solution[cell];
                    return value === null || value === undefined ? t("flashCards.crosswordBlankWord") : String(value);
                }),
            }),
    };
}

/** The words in the interface language. */
export function useCrosswordText(): CrosswordText {
    return makeCrosswordText(useTranslation());
}
